import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { isGenerationProposal, Turn } from "../../util/slopifyThread";
import FieldDiffRow from "./FieldDiffRow";
import { buildDiffGroups } from "./diff/diffGroups";
import { StructuredGroupCard } from "./diff/StructuredDiff";
import { useScopeContext } from "./ScopeContext";

interface ProposalCardProps {
  turn: Turn;
  manuallyDirtied: Set<string>;
  onAccept: (turnId: string, fieldNames: string[]) => void;
  onReject: (turnId: string) => void;
  onRefine: (turn: Turn) => void;
}

const formatRelative = (t: (k: string, opts?: any) => string, ms: number): string => {
  const seconds = Math.max(0, Math.round((Date.now() - ms) / 1000));
  if (seconds < 60) return t("general.assistantScenarIa.relativeSeconds", { count: seconds });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t("general.assistantScenarIa.relativeMinutes", { count: minutes });
  const hours = Math.round(minutes / 60);
  return t("general.assistantScenarIa.relativeHours", { count: hours });
};

export const ProposalCard = ({
  turn,
  manuallyDirtied,
  onAccept,
  onReject,
  onRefine,
}: ProposalCardProps) => {
  const { t } = useTranslation();
  const { modelName } = useScopeContext();

  const generationProposal = isGenerationProposal(turn.proposal) ? turn.proposal : null;
  const fields = generationProposal?.fields ?? [];
  const changedFields = useMemo(
    () => fields.filter((f) => !f.unchanged).map((f) => f.fieldName),
    [fields],
  );
  const [checked, setChecked] = useState<Set<string>>(() => new Set(changedFields));

  // Fold the flat field list into render groups (fused quizz array cards +
  // plain text rows). Each group's changed members drive its checkbox.
  const groups = useMemo(
    () => buildDiffGroups(modelName, fields, t as any),
    [modelName, fields, t],
  );

  // Re-sync if the proposal is swapped in place (defensive).
  useEffect(() => {
    setChecked(new Set(changedFields));
  }, [turn.id, changedFields]);

  // Re-render relative-time roughly every 10s so the header stays fresh.
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => forceTick((n) => n + 1), 10000);
    return () => window.clearInterval(id);
  }, []);

  const relative = formatRelative(t as any, turn.createdAt);

  // A group's changed members; the group's checkbox reflects/toggles all of them.
  const groupChangedFields = (group: (typeof groups)[number]) =>
    group.members.filter((m) => !m.unchanged).map((m) => m.fieldName);
  const isGroupChecked = (group: (typeof groups)[number]) => {
    const changed = groupChangedFields(group);
    return changed.length > 0 && changed.every((fn) => checked.has(fn));
  };
  // Count selected groups (friendlier than counting individual fields).
  const checkedCount = groups.filter((g) => isGroupChecked(g)).length;

  const handleToggle = (name: string, next: boolean) => {
    setChecked((prev) => {
      const out = new Set(prev);
      if (next) out.add(name);
      else out.delete(name);
      return out;
    });
  };

  const handleToggleGroup = (group: (typeof groups)[number], next: boolean) => {
    const changed = groupChangedFields(group);
    setChecked((prev) => {
      const out = new Set(prev);
      for (const fn of changed) {
        if (next) out.add(fn);
        else out.delete(fn);
      }
      return out;
    });
  };

  const handleAccept = () => {
    onAccept(turn.id, Array.from(checked));
  };

  return (
    <div className="rounded-md border border-green-200 bg-white shadow-sm p-2 mb-2 slopify-slide-in">
      <div className="flex items-center gap-1 text-[11px] text-gray-600">
        <span className="font-medium text-green-700">
          {t("general.assistantScenarIa.proposalHeader")}
        </span>
        <span className="text-gray-300">·</span>
        <span>{relative}</span>
      </div>
      {turn.prompt && (
        <div className="mt-1 text-[11px] italic text-gray-600 break-words">“{turn.prompt}”</div>
      )}

      <div className="mt-2">
        {fields.length === 0 ? (
          <div className="text-[11px] text-gray-500">
            {t("general.assistantScenarIa.proposalEmpty")}
          </div>
        ) : (
          groups.map((group) =>
            group.kind === "text" ? (
              <FieldDiffRow
                key={group.id}
                field={group.members[0]}
                checked={checked.has(group.members[0].fieldName)}
                onToggle={handleToggle}
                manuallyDirtied={manuallyDirtied.has(group.members[0].fieldName)}
              />
            ) : (
              <StructuredGroupCard
                key={group.id}
                group={group}
                checked={isGroupChecked(group)}
                onToggle={(next) => handleToggleGroup(group, next)}
                manuallyDirtied={group.fieldNames.some((fn) => manuallyDirtied.has(fn))}
              />
            ),
          )
        )}
      </div>

      <div className="mt-2 flex items-center gap-2 pt-2 border-t border-gray-100">
        <button
          type="button"
          className="text-[11px] px-2 py-1 rounded-md bg-green-500 text-white hover:bg-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={checkedCount === 0}
          onClick={handleAccept}
        >
          {t("general.assistantScenarIa.apply", { count: checkedCount })}
        </button>
        <button
          type="button"
          className="text-[11px] px-2 py-1 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100"
          onClick={() => onReject(turn.id)}
        >
          {t("general.assistantScenarIa.rejectProposal")}
        </button>
        <button
          type="button"
          className="text-[11px] px-2 py-1 rounded-md text-gray-600 hover:text-gray-800 hover:bg-gray-100 ml-auto"
          onClick={() => onRefine(turn)}
        >
          {t("general.assistantScenarIa.refine")}
        </button>
      </div>
    </div>
  );
};

export default ProposalCard;
